import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { useTemplate, useUpdateTemplate, useActivateTemplate, useRetireTemplate, useDeleteTemplate } from '../../../features/schemes/network/templates.api';
import type { SchemeTemplate } from '../../../features/schemes/network/templates.api';
import { Button } from '../../../components/ui/Button';
import { PageHeader } from '../../../components/layout/PageHeader';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { Money } from '../../../components/ui/Money';
import { Card, CardContent } from '../../../components/ui/Card';
import { Skeleton } from '../../../components/ui/Skeleton';
import { DataView } from '../../../components/ui/DataView';
import { usePageTitle } from '../../../lib/hooks';
import { useToast } from '../../../components/ui/Toast';
import Decimal from 'decimal.js';

export const Route = createFileRoute('/_auth/templates/$id')({
  component: TemplateDetail,
});

function TemplateDetail() {
  usePageTitle('Scheme Template');
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const query = useTemplate(id);

  const updateMutation = useUpdateTemplate(id);
  const activateMutation = useActivateTemplate();
  const retireMutation = useRetireTemplate();
  const deleteMutation = useDeleteTemplate();

  const onActivate = async (templateId: string) => {
    try {
      await activateMutation.mutateAsync({ id: templateId });
      toast({ type: 'success', title: 'Success', description: 'Template activated. It can now be used to create groups.' });
    // allow-silent-catch: surfaced via toast
    } catch (err: unknown) {
      toast({ type: 'error', title: 'Error', description: err instanceof Error ? err.message : 'Failed to activate template.' });
    }
  };

  const onRetire = async (templateId: string) => {
    try {
      await retireMutation.mutateAsync({ id: templateId });
      toast({ type: 'success', title: 'Success', description: 'Template retired. Existing groups keep their snapshot.' });
    // allow-silent-catch: surfaced via toast
    } catch (err: unknown) {
      toast({ type: 'error', title: 'Error', description: err instanceof Error ? err.message : 'Failed to retire template.' });
    }
  };

  const onDelete = async (templateId: string) => {
    try {
      await deleteMutation.mutateAsync({ id: templateId });
      toast({ type: 'success', title: 'Success', description: 'Template deleted.' });
      void navigate({ to: '/templates' });
    // allow-silent-catch: surfaced via toast
    } catch (err: unknown) {
      toast({ type: 'error', title: 'Error', description: err instanceof Error ? err.message : 'Failed to delete template.' });
    }
  };

  return (
    <DataView
      query={query}
      loadingSkeleton={<Skeleton rows={6} />}
    >
      {(templateRaw) => {
        const template = templateRaw as SchemeTemplate;
        return (
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <Link to="/templates" className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors w-fit">
                <ChevronLeft className="h-4 w-4" />Back
              </Link>
              <div className="flex items-center gap-2">
                {template.status === 'DRAFT' && (
                  <Button variant="primary" onClick={() => onActivate(template.id)} disabled={activateMutation.isPending}>
                    Activate
                  </Button>
                )}
                {template.status === 'ACTIVE' && (
                  <>
                    <Button
                      variant="secondary"
                      disabled={updateMutation.isPending}
                      onClick={async () => {
                        // Retouch with identical values: bumps the version so downstream consumers
                        // can tell the definition revision apart without any term change.
                        try {
                          await updateMutation.mutateAsync({
                            name: template.name,
                            commissionRate: new Decimal(template.commissionRate).toNumber(),
                            ...(template.maxBidDiscountRate !== null ? { maxBidDiscountRate: new Decimal(template.maxBidDiscountRate).toNumber() } : {}),
                          });
                          toast({ type: 'success', title: 'Success', description: 'Template revision saved.' });
                        // allow-silent-catch: surfaced via toast
                        } catch (err: unknown) {
                          toast({ type: 'error', title: 'Error', description: err instanceof Error ? err.message : 'Failed to save template.' });
                        }
                      }}
                    >
                      Save Revision
                    </Button>
                    <Button variant="danger" onClick={() => onRetire(template.id)} disabled={retireMutation.isPending}>
                      Retire
                    </Button>
                  </>
                )}
                {(template.status === 'DRAFT' || template.status === 'RETIRED') && (
                  <Button variant="ghost" className="text-danger-500 hover:text-danger-700" onClick={() => onDelete(template.id)} disabled={deleteMutation.isPending}>
                    Delete
                  </Button>
                )}
              </div>
            </div>

            <PageHeader
              title={template.name}
              subtitle={`Version ${template.version}`}
              action={
                <div className="flex items-center gap-3">
                  <StatusBadge status={template.status === 'ACTIVE' ? 'success' : template.status === 'DRAFT' ? 'warning' : 'info'} label={template.status} />
                </div>
              }
            />

            <Card className="border border-surface-3 shadow-sm rounded-sm">
              <CardContent className="p-6 grid grid-cols-2 md:grid-cols-4 gap-6 bg-surface-1 text-sm">
                <div><p className="text-text-secondary">Chit Value</p><Money paise={template.chitValue} className="font-mono font-medium" /></div>
                <div><p className="text-text-secondary">Members</p><p className="font-medium">{template.subscriberCount}</p></div>
                <div><p className="text-text-secondary">Duration</p><p className="font-medium">{template.durationCycles} cycles</p></div>
                <div><p className="text-text-secondary">Frequency</p><p className="font-medium">{template.cycleFrequency}</p></div>
                <div><p className="text-text-secondary">Commission</p><p className="font-mono font-medium">{new Decimal(template.commissionRate).mul(100).toFixed(2)}%</p></div>
                <div><p className="text-text-secondary">Max Bid Discount</p><p className="font-mono font-medium">{template.maxBidDiscountRate ? `${new Decimal(template.maxBidDiscountRate).mul(100).toFixed(2)}%` : '—'}</p></div>
                <div><p className="text-text-secondary">Auction Type</p><p className="font-medium">{template.auctionType}</p></div>
                <div><p className="text-text-secondary">Bidding Window</p><p className="font-medium">{template.biddingWindowMinutes ? `${template.biddingWindowMinutes} min` : '—'}</p></div>
                <div><p className="text-text-secondary">Foreman Ticket</p><p className="font-medium">{template.hasForemanTicket ? `Yes${template.foremanTicketIsPaying ? ' (paying)' : ''}` : 'No'}</p></div>
                <div><p className="text-text-secondary">Surety Required</p><p className="font-medium">{template.requiresSurety ? `Yes (max ${template.maxSuretiesPerSubscriber})` : 'No'}</p></div>
                <div><p className="text-text-secondary">TDS</p><p className="font-medium">{template.tdsApplicable ? (template.tdsRate ? `${new Decimal(template.tdsRate).mul(100).toFixed(2)}%` : 'Yes') : 'No'}</p></div>
                <div><p className="text-text-secondary">GST</p><p className="font-medium">{template.gstApplicable ? 'Yes' : 'No'}</p></div>
              </CardContent>
            </Card>

            <p className="text-xs text-text-muted">
              Groups created from this template hold an immutable copy of these terms. Editing the
              template affects only groups created afterwards.
            </p>
          </div>
        );
      }}
    </DataView>
  );
}
